import React, { FC } from 'react';
import { SidebarHeading } from '../../SidebarHeading';
import { Link as LinkIcon } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { StyledLink } from '../../../../../Components';
import { useReferencesContext } from '../ReferencesContext';
import { useGuideCmsContext } from '../../../GuideCmsContext';
import { StyledHeader } from '../styles';
import { JSONContent } from '@tiptap/core';
import { InternalNodeLink } from './InternalNodeLink';
import { useCitationIndex } from '../../../tiptap/citationNumbering';
import { RichText } from '../../../../../components/richText';

export const References: FC = () => {
  const { references } = useReferencesContext();

  return (
    <div>
      <SidebarHeading icon={<LinkIcon />}>Referanser</SidebarHeading>
      {references.guideLinks.length > 0 && (
        <>
          <StyledHeader>Anvisninger</StyledHeader>
          {references.guideLinks.map((node, index) => (
            <div key={index}>
              <StyledLink to={`/guide/${node.attrs?.guideId}`}>{node.attrs?.docName}</StyledLink>
            </div>
          ))}
        </>
      )}
      {references.standardLinks.length > 0 && (
        <>
          <StyledHeader>Standarder</StyledHeader>
          {references.standardLinks.map((node, index) => (
            <div key={index}>
              <StyledLink to={`/standard/${node.attrs?.standardId}`}>{node.attrs?.standardName}</StyledLink>
            </div>
          ))}
        </>
      )}
      <InternalReferencesSection nodes={references.internalHeadingLinks} heading="Punkter" />
      <InternalReferencesSection nodes={references.internalTableLinks} heading="Tabeller" />
      <InternalReferencesSection nodes={references.internalFigureLinks} heading="Figurer" />
      <InternalReferencesSection nodes={references.internalAppLinks} heading="Apper" />
      {references.citationLinks.length > 0 && (
        <>
          <StyledHeader>Litteraturhenvisninger</StyledHeader>
          {references.citationLinks.map((node, index) => (
            <CitationReference key={index} citationId={node.attrs?.citationId} />
          ))}
        </>
      )}
    </div>
  );
};

const CitationReference: FC<{ citationId: string }> = ({ citationId }) => {
  const { getCitationText, editor } = useGuideCmsContext();
  const index = useCitationIndex(citationId, editor ?? undefined);
  const text = getCitationText(citationId);
  if (text === null) return null;
  const label = index != null ? `[${index}]` : '[?]';

  const scrollToFirstOccurrence = () => {
    if (!editor) return;
    const { doc } = editor.state;
    let foundPos: number | null = null;
    doc.descendants((desNode, pos) => {
      if (foundPos !== null) return false;
      if (desNode.type.name === 'citationLink' && desNode.attrs.citationId === citationId) {
        foundPos = pos;
        return false;
      }
    });
    if (foundPos === null) return;
    editor.chain().focus().setTextSelection(foundPos).scrollIntoViewWithOffset().run();
  };

  return (
    <div>
      <StyledLink to="" onClick={(e) => { e.preventDefault(); scrollToFirstOccurrence(); }}>
        <Typography variant="body2" component="span">
          <strong>{label}</strong> <RichText html={text} />
        </Typography>
      </StyledLink>
    </div>
  );
};

const InternalReferencesSection: FC<{ nodes: JSONContent[]; heading: string }> = ({ nodes, heading }) => {
  if (nodes.length === 0) return null;
  return (
    <>
      <StyledHeader>{heading}</StyledHeader>
      {nodes.map((node, index) => (
        <InternalNodeLink key={index} id={node.attrs?.id} />
      ))}
    </>
  );
};
